import { useState, useRef, useEffect } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

/**
 * Mic button that records audio via MediaRecorder, ships it to
 * `POST /api/voice/transcribe`, and appends the transcript at the
 * current cursor position of the target textarea (or the end of the
 * value if no caret).
 *
 * Click-to-start / click-to-stop UX — matches Emergent's own chat
 * composer so it feels native. While recording, the button turns red
 * and pulses; on stop, it shows a spinner until Whisper returns.
 *
 * Props
 *   value: string                — current textarea value
 *   onChange: (next: string) => void
 *   inputRef: React.RefObject<HTMLTextAreaElement>  (optional — we use
 *     it to insert text at the user's cursor instead of clobbering)
 *   disabled: boolean
 *   testId: string                — base data-testid; we suffix "-mic"
 *
 * Permissions: requests `getUserMedia({audio: true})` on first click;
 * if denied, surfaces a friendly toast and stays out of the user's way.
 *
 * Limits: 25 MB max per clip (Whisper hard cap). We don't enforce a
 * client-side time limit — at typical opus bitrates the ceiling is ~30
 * minutes which is way beyond any reasonable dictation. The backend
 * still rejects oversize clips with a 413.
 */
export default function VoiceMicButton({
  value,
  onChange,
  inputRef,
  disabled = false,
  testId = "voice",
}) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false); // transcription in flight
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  // Clean up any open MediaRecorder if the component unmounts mid-record
  // (e.g. user navigates away). Without this we'd leak the mic stream.
  useEffect(() => {
    return () => {
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      } catch { /* unmount cleanup — swallow */ }
      try {
        streamRef.current?.getTracks?.().forEach(t => t.stop());
      } catch { /* unmount cleanup — swallow */ }
    };
  }, []);

  const handleStart = async () => {
    if (recording || busy) return;
    // Browser support check — older Safari and some embedded webviews
    // don't expose MediaRecorder. Tell the user why nothing happened.
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === "undefined") {
      toast.error("Voice input isn't supported on this browser. Use Chrome, Edge, Firefox, or Safari 14+.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Pick the best supported MIME — webm/opus is Chrome+FF, mp4 is
      // Safari. We leave `mimeType` unset on `new MediaRecorder(stream)`
      // to let the browser pick its native default, which Whisper
      // accepts for all 7 formats it supports.
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = async () => {
        // Stop the mic immediately so the browser tab indicator goes
        // away — feels responsive even before Whisper returns.
        try { streamRef.current?.getTracks?.().forEach(t => t.stop()); } catch { /* best-effort */ }
        streamRef.current = null;

        const mime = mr.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        if (blob.size < 500) {
          // Sub-half-KB clip → essentially silence / accidental tap.
          // Spare the user a useless Whisper round-trip + toast.
          return;
        }

        setBusy(true);
        try {
          const ext = mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm";
          const form = new FormData();
          form.append("audio", blob, `recording.${ext}`);
          const res = await axios.post(`${API}/voice/transcribe`, form, {
            withCredentials: true,
            // Whisper's typical latency is ~1-2s for a 30s clip but can
            // spike to 10-15s under load — give it room before we
            // surface a confusing "Couldn't reach the server" error.
            timeout: 60_000,
          });
          const text = (res.data?.text || "").trim();
          if (!text) {
            toast.info("Didn't catch that — try again a little closer to the mic.");
            return;
          }
          // Splice the transcript in at the cursor if the textarea is
          // focused & we know the selection; otherwise append to the end
          // with a space so we never silently wipe existing text.
          const el = inputRef?.current;
          if (el && typeof el.selectionStart === "number" && document.activeElement === el) {
            const before = value.slice(0, el.selectionStart);
            const after = value.slice(el.selectionEnd);
            const sep = before && !/\s$/.test(before) ? " " : "";
            const next = before + sep + text + after;
            onChange(next);
            // Move caret to end of inserted text on the next tick so the
            // setState above has flushed to the textarea.
            const caret = before.length + sep.length + text.length;
            setTimeout(() => {
              try {
                el.focus();
                el.setSelectionRange(caret, caret);
              } catch { /* caret restore is best-effort */ }
            }, 0);
          } else {
            const sep = value && !/\s$/.test(value) ? " " : "";
            onChange(value + sep + text);
          }
        } catch (err) {
          const detail = err.response?.data?.detail;
          toast.error(typeof detail === "string" ? detail : "Transcription failed. Try again.");
        } finally {
          setBusy(false);
        }
      };

      mr.start();
      setRecording(true);
    } catch (err) {
      // Most common cause: user denied mic permission. The Permission
      // dialog is browser-native so we just tell them how to grant it.
      const msg = err?.name === "NotAllowedError"
        ? "Microphone permission was denied. Enable it in your browser settings to dictate."
        : "Couldn't access the microphone. Check it's connected and try again.";
      toast.error(msg);
    }
  };

  const handleStop = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch { /* state may have flipped to inactive between check and stop */ }
    setRecording(false);
  };

  const onClick = () => (recording ? handleStop() : handleStart());

  const ariaLabel = recording
    ? "Stop recording"
    : busy
    ? "Transcribing"
    : "Start voice input";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      aria-label={ariaLabel}
      title={ariaLabel}
      data-testid={`${testId}-mic`}
      data-recording={recording ? "true" : "false"}
      className={[
        "inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors flex-shrink-0",
        recording
          ? "bg-red-50 border-red-300 text-red-600 animate-pulse"
          : busy
          ? "bg-gray-50 border-gray-200 text-gray-400"
          : "bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700",
        disabled ? "opacity-40 cursor-not-allowed" : "",
      ].join(" ")}
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : recording ? (
        <Square className="w-3 h-3 fill-current" />
      ) : (
        <Mic className="w-3.5 h-3.5" />
      )}
    </button>
  );
}
